/**
 * Offline Sessions tab.
 *
 * Enqueues historical charging sessions for replay against the live CSMS
 * once the station boots. The flow:
 *   1. Fill the form, click Add → row appears in the queue table.
 *   2. Connect to the CSMS (Charge Point tab).
 *   3. Click Flush now → OfflineSessionRunner walks each row through
 *      StartTransaction → MeterValues → StopTransaction; rows that succeed
 *      are deleted from the queue.
 *
 * Schema matches data/storage-schema.yaml#offlineQueue exactly.
 */
import { useEffect, useState } from 'react';
import { ConnectionState, StopReason, stopReasonsForVersion } from '@/state/types';
import { useStationStore } from '@/state/useStationStore';
import { buildOfflineSnapshot, useOfflineQueueStore } from '@/state/useOfflineQueueStore';
import { useConfirm } from '@/hooks/useConfirm';
import { toast } from '@/state/useToastStore';
import { pluralize } from '@/lib/format';

interface FormState {
  agoSec: string;
  duration: string;
  idTag: string;
  connectorId: string;
  meterStartWh: string;
  energyDeliveredWh: string;
  meterValueIntervalSec: string;
  voltage: string;
  startSoC: string;
  endSoC: string;
  stopReason: StopReason;
}

const DEFAULT_FORM: FormState = {
  agoSec: '3600',
  duration: '1800',
  idTag: 'TAG-1',
  connectorId: '1',
  meterStartWh: '0',
  energyDeliveredWh: '7400',
  meterValueIntervalSec: '300',
  voltage: '230',
  startSoC: '20',
  endSoC: '80',
  stopReason: StopReason.Local,
};

function uuidish(): string {
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function OfflineSessionTab(): JSX.Element {
  const stationId = useStationStore((s) => s.stationId);
  const connectionState = useStationStore((s) => s.connectionState);
  const ocppVersion = useStationStore((s) => s.ocppVersion);
  const createUploadPort = useStationStore((s) => s.createUploadPort);
  // Only offer reasons legal on this station's OCPP version (1.6 rejects
  // the 2.x-only ReasonEnumType additions).
  const stopReasons = stopReasonsForVersion(ocppVersion);
  const entries = useOfflineQueueStore((s) => s.entries);
  const status = useOfflineQueueStore((s) => s.status);
  const lastReport = useOfflineQueueStore((s) => s.lastReport);
  const refresh = useOfflineQueueStore((s) => s.refresh);
  const addManual = useOfflineQueueStore((s) => s.addManual);
  const remove = useOfflineQueueStore((s) => s.remove);
  const clearAll = useOfflineQueueStore((s) => s.clearAll);
  const flushNow = useOfflineQueueStore((s) => s.flushNow);

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirm();

  useEffect(() => {
    void refresh(stationId);
  }, [refresh, stationId]);

  const onChange =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleAdd = (): void => {
    setError(null);
    try {
      const snap = buildOfflineSnapshot({
        queueId: uuidish(),
        agoSec: Number(form.agoSec),
        duration: Number(form.duration),
        idTag: form.idTag,
        connectorId: Number(form.connectorId),
        meterStartWh: Number(form.meterStartWh),
        energyDeliveredWh: Number(form.energyDeliveredWh),
        meterValueIntervalSec: Number(form.meterValueIntervalSec),
        voltage: Number(form.voltage),
        stopReason: form.stopReason,
        ...(form.startSoC !== '' ? { startSoC: Number(form.startSoC) } : {}),
        ...(form.endSoC !== '' ? { endSoC: Number(form.endSoC) } : {}),
        nowMs: Date.now(),
      });
      void addManual(stationId, snap).then(() => toast('Added to queue'));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleFlush = (): void => {
    setError(null);
    const port = createUploadPort();
    if (!port) {
      setError('Connect the station first — no live adapter to upload through.');
      return;
    }
    void flushNow(stationId, port).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : String(err));
    });
  };

  const handleClearAll = (): void => {
    if (entries.length === 0) return;
    confirm(`delete all ${pluralize(entries.length, 'queued session')}`, () => {
      void clearAll(stationId).then(() => toast('Cleared offline queue'));
    });
  };

  const canFlush = connectionState === ConnectionState.Connected && entries.length > 0;

  return (
    <div
      role="tabpanel"
      id="tab-panel-offline"
      aria-labelledby="tab-offline"
      className="space-y-4 p-4"
      data-testid="offline-session-tab"
    >
      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <h2 className="mb-2 text-sm font-medium text-slate-100">New offline session</h2>
        <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3 md:grid-cols-4">
          <Field
            label="Ago (s)"
            value={form.agoSec}
            onChange={onChange('agoSec')}
            testid="form-agoSec"
          />
          <Field
            label="Duration (s)"
            value={form.duration}
            onChange={onChange('duration')}
            testid="form-duration"
          />
          <Field
            label="idTag"
            value={form.idTag}
            onChange={onChange('idTag')}
            testid="form-idTag"
          />
          <Field
            label="Connector"
            value={form.connectorId}
            onChange={onChange('connectorId')}
            testid="form-connectorId"
          />
          <Field
            label="Meter start Wh"
            value={form.meterStartWh}
            onChange={onChange('meterStartWh')}
            testid="form-meterStartWh"
          />
          <Field
            label="Energy Wh"
            value={form.energyDeliveredWh}
            onChange={onChange('energyDeliveredWh')}
            testid="form-energyDeliveredWh"
          />
          <Field
            label="MV interval (s)"
            value={form.meterValueIntervalSec}
            onChange={onChange('meterValueIntervalSec')}
            testid="form-meterValueIntervalSec"
          />
          <Field
            label="Voltage V"
            value={form.voltage}
            onChange={onChange('voltage')}
            testid="form-voltage"
          />
          <Field
            label="Start SoC %"
            value={form.startSoC}
            onChange={onChange('startSoC')}
            testid="form-startSoC"
          />
          <Field
            label="End SoC %"
            value={form.endSoC}
            onChange={onChange('endSoC')}
            testid="form-endSoC"
          />
          <label className="flex flex-col gap-1">
            <span className="text-slate-500">Stop reason</span>
            <select
              value={form.stopReason}
              onChange={onChange('stopReason')}
              className="rounded border border-slate-600 bg-slate-950 px-2 py-1 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
              data-testid="form-stopReason"
            >
              {stopReasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={handleAdd}
            className="rounded bg-primary-base px-3 py-1 text-xs font-medium text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base"
            data-testid="offline-add"
          >
            Add to queue
          </button>
          {error ? (
            <span
              className="rounded bg-rose-900/40 px-2 py-1 text-[10px] text-rose-200"
              data-testid="offline-form-error"
            >
              {error}
            </span>
          ) : null}
        </div>
      </section>

      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <header className="mb-2 flex items-baseline justify-between">
          <h2 className="text-sm font-medium text-slate-100">Queue</h2>
          <span className="text-[10px] text-slate-500" data-testid="offline-count">
            {entries.length} queued
          </span>
        </header>
        {entries.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="offline-empty">
            Queue is empty.
          </p>
        ) : (
          <table className="w-full text-left text-xs" data-testid="offline-table">
            <thead className="text-slate-500">
              <tr>
                <th className="px-2 py-1">queueId</th>
                <th className="px-2 py-1">idTag</th>
                <th className="px-2 py-1">conn</th>
                <th className="px-2 py-1">duration</th>
                <th className="px-2 py-1">energy</th>
                <th className="px-2 py-1">status</th>
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {entries.map((s) => (
                <tr
                  key={s.queueId}
                  className="border-t border-slate-800"
                  data-testid={`offline-row-${s.queueId}`}
                >
                  <td className="px-2 py-1 font-mono text-slate-100">{s.queueId.slice(0, 8)}…</td>
                  <td className="px-2 py-1 text-slate-300">{s.idTag}</td>
                  <td className="px-2 py-1 text-slate-300">{String(s.connectorId)}</td>
                  <td className="px-2 py-1 text-slate-300">{s.duration}s</td>
                  <td className="px-2 py-1 text-slate-300">{s.energyDeliveredWh} Wh</td>
                  <td className="px-2 py-1 text-slate-300">{s.status}</td>
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        confirm(`delete queued session ${s.queueId.slice(0, 8)}`, () => {
                          void remove(stationId, s.queueId);
                        });
                      }}
                      className="rounded bg-rose-700 px-2 py-0.5 text-[10px] text-rose-50 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300"
                      data-testid={`offline-delete-${s.queueId}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            disabled={!canFlush || status === 'flushing'}
            onClick={handleFlush}
            className="rounded bg-primary-base px-3 py-1 text-xs font-medium text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="offline-flush"
          >
            {status === 'flushing' ? 'Flushing…' : 'Flush now'}
          </button>
          <button
            type="button"
            disabled={entries.length === 0}
            onClick={handleClearAll}
            className="rounded bg-slate-700 px-3 py-1 text-xs text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="offline-clear"
          >
            Clear all
          </button>
          {!canFlush && entries.length > 0 ? (
            <span className="text-[10px] text-slate-500">
              Connect first to flush queued sessions.
            </span>
          ) : null}
          {lastReport ? (
            <span className="text-[10px] text-slate-400" data-testid="offline-last-report">
              last: attempted={lastReport.attempted} · uploaded={lastReport.uploaded} · failed=
              {lastReport.failed}
            </span>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  testid,
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  testid: string;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-slate-500">{label}</span>
      <input
        type="text"
        value={value}
        onChange={onChange}
        className="rounded border border-slate-600 bg-slate-950 px-2 py-1 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
        data-testid={testid}
      />
    </label>
  );
}
